import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BriefcaseBusiness, LogOut, Menu, X, LayoutDashboard } from "lucide-react";
import { useState, useEffect } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isLanding = location.pathname === "/";
  const isDarkPage = [].includes(location.pathname);

  // Landing page has white hero - always use dark text
  const navBg = isLanding
    ? scrolled
      ? "bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm"
      : "bg-white/80 backdrop-blur-md border-b border-gray-100"
    : isDarkPage
    ? "bg-[#0A1128]/95 backdrop-blur-xl border-b border-white/5"
    : "bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm";

  const useDark = isDarkPage;
  const textColor = useDark ? "text-white" : "text-[#0A1128]";
  const textMuted = useDark ? "text-white/60" : "text-gray-500";
  const mobileBg = useDark ? "bg-[#0A1128]" : "bg-white";
  const mobileBorder = useDark ? "border-white/5" : "border-gray-100";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${navBg}`} data-testid="navbar">
      {/* Same container geometry as the footer: logo hugs the left edge,
          actions hug the right — bookending the page consistently. */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-16 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="navbar-logo">
          <img src="/logo.png" alt="PlacementOS" className="h-10 w-auto" style={{ maxHeight: '40px' }} />
        </Link>

        {/* Desktop */}
        <div className="hidden sm:flex items-center gap-1">
          <Link 
            to="/about" 
            className={`text-sm ${textMuted} hover:text-[#5B3DF5] px-3 py-2 transition-colors`}
          >
            About
          </Link>
          <Link 
            to="/faq" 
            className={`text-sm ${textMuted} hover:text-[#5B3DF5] px-3 py-2 transition-colors`}
          >
            FAQ
          </Link>
          <Link 
            to="/contact" 
            className={`text-sm ${textMuted} hover:text-[#5B3DF5] px-3 py-2 transition-colors`}
          >
            Contact
          </Link>
          <Link
            to="/season-pass"
            className={`text-sm ${textMuted} hover:text-[#5B3DF5] px-3 py-2 transition-colors`}
          >
            Pricing
          </Link>
          {!user && (
            <Link
              to="/auth?role=recruiter"
              className={`text-sm ${textMuted} hover:text-[#5B3DF5] px-3 py-2 transition-colors inline-flex items-center gap-1`}
              data-testid="recruiter-nav-link"
            >
              <BriefcaseBusiness className="w-4 h-4" /> Recruiters
            </Link>
          )}
          {/* Blog is a static nginx dir, not a React route — plain anchor */}
          <a
            href="/blog/"
            className={`text-sm ${textMuted} hover:text-[#5B3DF5] px-3 py-2 transition-colors`}
            data-testid="blog-nav-link"
          >
            Blog
          </a>
          {user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                data-testid="dashboard-nav-btn"
                className={`${textMuted} hover:${textColor} hover:bg-black/5`}
              >
                <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
              </Button>
              <span className={`text-sm ${textMuted} max-w-[160px] truncate`}>{user.email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); navigate("/"); }}
                data-testid="logout-btn"
                className={`${textMuted} hover:${textColor} hover:bg-black/5`}
              >
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <button
              onClick={() => navigate("/auth")}
              data-testid="login-nav-btn"
              className="btn-gradient text-white text-sm px-5 py-2 rounded-lg font-semibold border-0 cursor-pointer ml-2"
            >
              Get Started
            </button>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className={`sm:hidden ${textMuted} bg-transparent border-0 cursor-pointer`}
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className={`sm:hidden ${mobileBg} border-t ${mobileBorder} px-4 py-4 space-y-3`}>
          <Link 
            to="/about" 
            onClick={() => setMobileOpen(false)}
            className={`block text-sm ${textMuted} py-2`}
          >
            About
          </Link>
          <Link 
            to="/faq" 
            onClick={() => setMobileOpen(false)}
            className={`block text-sm ${textMuted} py-2`}
          >
            FAQ
          </Link>
          <Link 
            to="/contact" 
            onClick={() => setMobileOpen(false)}
            className={`block text-sm ${textMuted} py-2`}
          >
            Contact
          </Link>
          <Link
            to="/season-pass"
            onClick={() => setMobileOpen(false)}
            className={`block text-sm ${textMuted} py-2`}
          >
            Pricing
          </Link>
          {!user && (
            <Link
              to="/auth?role=recruiter"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 text-sm ${textMuted} py-2`}
            >
              <BriefcaseBusiness className="w-4 h-4" /> Recruiters
            </Link>
          )}
          <a
            href="/blog/"
            className={`block text-sm ${textMuted} py-2`}
          >
            Blog
          </a>
          {user ? (
            <>
              <p className={`text-sm ${textMuted} truncate`}>{user.email}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { navigate("/dashboard"); setMobileOpen(false); }}
                className={`${textMuted} w-full justify-start`}
              >
                <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); navigate("/"); setMobileOpen(false); }}
                className={`${textMuted} w-full justify-start`}
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </>
          ) : (
            <button
              onClick={() => { navigate("/auth"); setMobileOpen(false); }}
              className="btn-gradient text-white text-sm px-5 py-2 rounded-lg font-semibold border-0 cursor-pointer w-full"
            >
              Get Started
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
